import { ChevronLeft, Command, Search } from "lucide-react";
import { useQuota } from "../api/queries";
import { useSessionStore } from "../session";
import { useTranslation } from "@/shared/i18n/I18nProvider";
import { Logo } from "./Logo";
import { NotificationBell } from "./NotificationBell";

interface HeaderProps {
  title: string;
  /**
   * If true, render the mobile-only back chevron (md:hidden) with the
   * fade+slide-in transition. Layout flips this on once its in-content
   * back-link scrolls out of view.
   */
  showBack?: boolean;
  /** Callback when the header back chevron is clicked. */
  onBack?: () => void;
}

/**
 * Top header. Cmd+K listener lives in `CommandPalette` (mounted in
 * Layout.tsx); the "⌘ K" pill in the search slot is just a visual cue.
 *
 * The mobile back chevron is always mounted (so the cross-fade transition
 * has a fixed origin) but is `aria-hidden` + non-focusable + zero-width when
 * `showBack` is false, so screen-readers and keyboard nav skip it.
 */
export function Header({ title, showBack = false, onBack }: HeaderProps) {
  const session = useSessionStore((s) => s.session);
  const quota = useQuota(session?.workspaceId);
  const { t } = useTranslation();

  return (
    <header
      className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-default px-4 backdrop-blur sm:px-6"
      style={{
        // ~85% elevated for the glass effect (backdrop-blur composites the
        // scroll content through). color-mix keeps it token-driven +
        // theme-aware; a flat var()+hex-alpha concat is invalid CSS.
        // style-ok: color-mix glass
        backgroundColor: "color-mix(in srgb, var(--color-elevated) 85%, transparent)",
      }}
    >
      <div className="flex min-w-0 items-center gap-3">
        {/* Mobile-only back chevron. Animated via width + opacity so the
            title slides right when it appears (iOS large-title pattern). */}
        <button
          type="button"
          onClick={onBack}
          aria-label={t("common.back")}
          aria-hidden={!showBack}
          tabIndex={showBack ? 0 : -1}
          data-testid="header-back"
          className="flex items-center overflow-hidden text-fg transition-all duration-200 ease-out md:hidden"
          style={{
            width: showBack ? 28 : 0,
            opacity: showBack ? 1 : 0,
            transform: showBack ? "translateX(0)" : "translateX(-8px)",
            pointerEvents: showBack ? "auto" : "none",
          }}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        {/* Logo only shows on narrow viewports — Sidebar already has it on md+.
            Hide it on depth-2 so the back chevron sits flush against the title. */}
        {!showBack && (
          <span className="md:hidden">
            <Logo withWordmark={false} />
          </span>
        )}
        <h1 className="truncate text-base font-extrabold tracking-tight text-fg sm:text-lg">
          {title}
        </h1>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="Mở Command Palette (Cmd+K)"
          className="hidden items-center gap-2 rounded-lg border border-default px-3 py-1.5 text-xs text-fg-subtle transition-colors hover:bg-surface md:flex"
          // C2 from iSale-User-Flow-Index.md §6 — this button used to be a
          // `window.alert("Sprint 8")` stub even though CommandPalette had
          // shipped via keyboard shortcut. We dispatch a custom event the
          // palette listens for so we don't have to lift state up out of
          // CommandPalette (which is mounted once at the Layout level).
          onClick={() => window.dispatchEvent(new Event("isale:cmd-palette-open"))}
        >
          <Search className="h-3.5 w-3.5" />
          {t("common.search")}
          <span className="ml-2 flex items-center gap-0.5 text-3xs">
            <Command className="h-3 w-3" />K
          </span>
        </button>
        {quota.data && (
          <div className="pill">
            {quota.data.video_used}/{quota.data.video_cap} clip
          </div>
        )}
        {/* Notifications bell — desktop + mobile. Side drawer 320px;
            badge polls every 30s. Spec: iSale Notifications.html lines
            2261+. Wires to backend GET /v1/notifications. */}
        <NotificationBell />
      </div>
    </header>
  );
}
